import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Table, message } from "antd";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ExportData from "../components/ExportData";
import { PageToolbar } from "../components/PageToolbar";
import entitlements from "../config/entitlements";
import { ProductCategory } from "../helpers/shared-interfaces";
import { request , showTotalPagination } from "../helpers/utilities";
import { GET_PRODUCT_CATEGORIES } from "../gql/products";

interface State {
  isFiltering: boolean;
  productCategories: ProductCategory[];
  page: number;
  productCategoryCount: number;
  filteredproductCategories: ProductCategory[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export function ProductsCountRender(
  text: string,
  record: ProductCategory,
): JSX.Element {
  return <span>{record.products.length}</span>;
}

export const columns = [
  {
    title: "Product Category",
    dataIndex: "name",
    key: "Product Category",
    render(text: string, record: ProductCategory): JSX.Element {
      return (
        <NavLink
          to={`/product-categories/${record.id}`}
          data-cy="test-product-category-link"
        >
          {text}
        </NavLink>
      );
    },
  },

  {
    title: "Number of Products",
    dataIndex: "products",
    key: "Number of Products",
    render: ProductsCountRender,
  },
];

export function ProductCategoriesTableFactory(
  data: ProductCategory[],
  count: number,
  page: number,
  nextPage: (page: number) => void,
  loading?: boolean,
): JSX.Element {
  const tempData: ProductCategory[] = data.map(
    (productCategory: ProductCategory): ProductCategory => {
      return { ...productCategory, key: productCategory.id };
    },
  );
  return (
    <Table
      loading={loading}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export default class AllProductCategories extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      productCategories: [],
      page: 1,
      productCategoryCount: 0,
      filteredproductCategories: [],
      isFiltering: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.redirectToNewProductCategoryPage =
      this.redirectToNewProductCategoryPage.bind(this);
    this.getAllProductCategories = this.getAllProductCategories.bind(this);
    this.filterProductCategories = this.filterProductCategories.bind(this);
    this.nextPage = this.nextPage.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  async filterProductCategories(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isFiltering: true });
    const filterBy: string = e.target.value;
    if (filterBy.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }
    const filtered: ProductCategory[] = this.state.productCategories.filter(
      function (productCategory: ProductCategory): boolean {
        if (
          productCategory.name.toLowerCase().includes(filterBy.toLowerCase())
        ) {
          return true;
        }
        return false;
      },
    );
    this.setState({ filteredproductCategories: filtered });
  }

  getAllProductCategories(): void {
    request({
      query: GET_PRODUCT_CATEGORIES,
      variables: {
        page: 1,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        productCategories: data.productCategories.results,
        productCategoryCount: data.productCategories.count,
        isLoading: false,
      });
    });
  }

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      productCategories: data.results as ProductCategory[],
      selectedFieldsToDisplay: fields,
      page: 1,
      productCategoryCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState({
      removedField: field,
      selectedFieldsToDisplay: fields,
    });

    if (fields.length === 0) this.getAllProductCategories();
  };

  redirectToNewProductCategoryPage(): void {
    this.props.history.push("/product-categories/new");
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllProductCategories();
    });
  }

  nextPage(page: number) {
    this.setState({ isLoading: true, page }, (): void => {
      this.getAllProductCategories();
    });
  }

  render(): JSX.Element | null {
    const {
      productCategories,
      isFiltering,
      filteredproductCategories,
      isLoading,
      selectedFieldsToDisplay,
      removedField,
      productCategoryCount,
      page,
    } = this.state;
    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="productCategory"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
        />

        <Card>
            <div className="product-categories-table-container">
              <PageToolbar
                searchValue={this.state.filterBy}
                onSearchChange={this.filterProductCategories}
                searchPlaceholder="Filter by name..."
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                actions={
                  <EntitledSection entitlement={entitlements.ProductCategories.create}>
                    <ExportData
                      model={"productCategory"}
                      dataArray={this.state.productCategories}
                    />
                  </EntitledSection>
                }
                primaryAction={
                  <EntitledSection entitlement={entitlements.ProductCategories.create}>
                    <Button
                      type="primary"
                      onClick={this.redirectToNewProductCategoryPage}
                      data-cy="test-new-product-category-button"
                    >
                      <PlusOutlined /> Create Product Category
                    </Button>
                  </EntitledSection>
                }
                totalCount={productCategoryCount}
                currentDataCount={isFiltering ? filteredproductCategories.length : productCategories.length}
                page={page}
                pageSize={25}
                modelName="product categories"
              />
              {ProductCategoriesTableFactory(
                isFiltering ? filteredproductCategories : productCategories,
                productCategoryCount,
                page,
                this.nextPage,
                isLoading,
              )}
            </div>
          </Card>
      </>
    );
  }
}
